import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import env from '../../../util/env';
import styled from 'styled-components';
import {contentIcAdd24px} from '@ey/motif-react-wrapper/assets/icons';
import {labels, currentResource} from '../../../util/utils';
import utilityfunctions from '../../../util/utilityfunctions';
import {serviceURL, displayLanguageListItems, promiseAreas, AdminRoles} from '../../../util/uiconstants';
import {getAllAdminUsers, clearAllAdminUsers} from '../../../actions/AdminUser/AdminUserAction';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import MotifSelect from '@ey/motif-react-wrapper/Select';
import MotifOption from '@ey/motif-react-wrapper/Option';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import CustomTable from '@ey/canvascoreservices/CustomTable';
import TrackerLoader from './../../Common/LoadingIndicator/TrackerLoader';
import {actionIcDelete24px} from '@ey/motif-react-wrapper/assets/icons';
import AddAdminModal from './AddAdminModal';
import {raiseJSValidationError} from '../../../actions/erroractions';
import Button from '../../CoreWrappers/Button';

const ManageAdmins = (props) => {
	const userPreferences = useSelector((store) => store.userPreferences);
	const [showAddAdminModal, setShowAddAdminModal] = useState(false);
	const dispatch = useDispatch();
	const adminUsers = useSelector((store) => store.adminUsers);

	useEffect(() => {
		if (
			userPreferences.isPreferencesLoaded &&
			(!userPreferences.userType === 'Admin' ||
				(!userPreferences.isAdminReader && !userPreferences.isAdminWriter && !userPreferences.isSuperAdmin))
		) {
			// this is to let the current resource acquire the language first and then display the message
			setTimeout(() => {
				dispatch(raiseJSValidationError(currentResource.AdminUserErrors.userError));
			}, 900);
		}
	}, [userPreferences]);

	useEffect(() => {
		let url = `${env.getBaseURL(serviceURL)}Admins`;
		dispatch(getAllAdminUsers(url));
		return () => {
			dispatch(clearAllAdminUsers(url));
		};
	}, []);

	const formatLabel = (data) => {
		return <span title={data.label}>{data.label}</span>;
	};

	const onAddUserClick = () => {
		setShowAddAdminModal(true);
	};

	const tableColumnsUser = [
		{
			label: labels.firstName,
			sortable: true,
			sort: 'asc',
			propName: 'firstName',
			width: '20%'
		},
		{
			label: labels.lastName,
			sortable: true,
			propName: 'lastName',
			width: '20%'
		},
		{
			label: labels.emailAddress,
			sortable: true,
			propName: 'emailAddress',
			width: '25%'
		},
		{
			label: labels.userRole,
			sortable: true,
			propName: 'userRole',
			width: '30%',
			customCellRender: (item) => {
				return (
					<MotifFormField>
						<MotifSelect
							multiple={false}
							name="motif-select-role"
							id="role"
							key={item.emailAddress}
							value={item.userRole?.value}
							onChange={(option) => {
								if (option && option.value !== '') {
								}
							}}
							className="manage-admin-dropdown"
							ariaLabelledBy="select-label"
							title={currentResource.AdminRoles.find((x) => x.id === item.userRole?.id)?.label}
						>
							{AdminRoles.map((role) => {
								return (
									<MotifOption className={'ellipsis'} title={role.label} key={role.id} value={role.value}>
										{currentResource.AdminRoles.find((x) => x.id === role.id)?.label}
									</MotifOption>
								);
							})}
						</MotifSelect>
					</MotifFormField>
				);
			}
		},
		{
			label: '',
			sortable: false,
			propName: '',
			customCellRender: () => {
				const result = (
					<MotifIcon
						id=""
						className="remove-row"
						// onClick={''}
						src={actionIcDelete24px}
						title={labels.remove}
					/>
				);
				return result;
			},
			width: '5%'
		}
	];

	return (
		<StyledManageAdminsSection className="customScrollbar">
			<section className="mainResponsiveWrapper">
				<header>
					<h3 className="ellipsis">{utilityfunctions.removeTags(labels.manageAdmins)}</h3>
				</header>
				<section className="instructional-btn-wrap">
					<p className="instructional-text ellipsis">{labels.manageAdminsInstructionalText}</p>
					<section className="add-button-wrap">
						<Button onClick={onAddUserClick} svg={contentIcAdd24px} title={labels.addNewAdmin} />
						<Button
							onClick={onAddUserClick}
							disabled={props.disabled}
							variant={'text-alt'}
							title={labels.addNewAdmin}
							label={labels.addNewAdmin}
						/>
					</section>
				</section>

				{showAddAdminModal && <AddAdminModal setShowAddAdminModal={setShowAddAdminModal} />}
				<TrackerLoader
					areas={[promiseAreas.GET_ADMIN_USERS]}
					view="__"
					styleName="AdminUsersLoader"
					fullscreen={false}
				/>
				<section className="user-details-wrap">
					<CustomTable
						columns={tableColumnsUser}
						className="admin-details-table"
						items={adminUsers}
						showAccordions={false}
						hoverShadow={false}
						showNoResultFoundMessage={false}
						theme={utilityfunctions.getMotifTheme()}
						noResultFoundMessage={<span className="empty-container">{labels.noResultFound}</span>}
						searchConfig={{
							disabled: true
						}}
						sortConfig={{
							sortBy: 'name',
							sortIsAscending: true
						}}
						paginationConfig={{
							disabled: adminUsers.length > 20 ? false : true,
							option: [5, 20, 100, 200],
							showMoreLabel: labels.show,
							currentPage: 1,
							pageSize: 20,
							firstButtonProps: labels.firstPage,
							lastButtonProps: labels.lastPage,
							prevButtonProps: labels.prevPage,
							nextButtonProps: labels.nextPage
						}}
					/>
				</section>
			</section>
		</StyledManageAdminsSection>
	);
};

export default ManageAdmins;

const StyledManageAdminsSection = styled.section`
	width: 100%;
	height: calc(100% - 4.9rem);
	margin-top: 5rem;
	header {
		width: 100%;
		h3 {
			margin-bottom: 0;
		}

		h4 {
			margin: 0;
			&.delegate-to-header {
				margin-top: 3rem;
			}
		}
	}
	.ellipsis {
		display: inline-block;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.empty-container {
		background: ${(props) => props.theme.color[`whiteblack`]};
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		text-align: center;
		font-size: 1.3rem;
		padding: 1.5rem 0;
		color: ${(props) => props.theme.color[`grey400`]};
	}

	.instructional-text {
		font-size: 1.4rem;
		max-width: 80%;
	}

	.table-row {
		&:hover {
			.remove-row {
				align-items: center;
				display: flex;
				svg {
					fill: ${(props) => props.theme.color[`black`]};
					width: 2rem !important;
					height: 2rem !important;
					max-width: 2.5rem !important;
					max-height: 2.5rem !important;
				}
			}
		}
	}

	.remove-row {
		display: none;
	}

	.instructional-btn-wrap {
		display: flex;
		justify-content: space-between;

		.add-button-wrap {
			display: flex;
			align-items: center;
			margin-bottom: 1rem;
			max-width: 20rem;
		}

		.motif-icon {
			width: 2rem !important;
			height: 2rem !important;
			svg {
				width: 2rem !important;
				height: 2rem !important;
				fill: ${(props) => props.theme.color[`blue600yellow400`]};
			}
		}
	}
	.admin-details-table {
		margin-top: 2rem;
		width: 100%;

		.list-body {
			max-height: 55rem;
		}

		.show-more-label,
		.motif-pagination .motif-dropdown-trigger {
			visibility: hidden;
		}
		.list-body {
			div {
				&:last-child {
					.table-row {
						border-bottom: 0;
					}
				}
			}
		}

		.motif-card {
			border: solid 1px ${(props) => props.theme.color[`grey300`]};
		}
		.empty-container,
		.table-row,
		.row-cell-0,
		.row-cell-1,
		.row-cell-2,
		.row-cell-3,
		.row-cell-4,
		.table-header-item {
			font-size: 1.4rem;
		}

		.row-cell-3 {
			overflow: unset;
			.motif-form-field {
				width: 80%;
				margin-bottom: 0;
			}
			.motif-select-trigger {
				height: 3.5rem;
				display: flex;
				-webkit-box-align: center;
				align-items: center;
				padding: 1rem 2rem 1rem 1rem;
				font-size: 1.4rem;
			}
			.motif-option-text {
				width: 100%;
				font-size: 1.4rem;
			}
			.motif-select-list-options {
				display: flex;
				flex-direction: column;
				.motif-option {
					padding: 0 1rem;
				}
			}
		}
	}
`;
